
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  PropType,
  ref,
  watch,
} from "vue";
import {
  cancelWizardSession,
  compactWizardSession,
  connectWizardSessionStream,
  buildDebugContextPayload,
  createWizardSession,
  disconnectWizardStream,
  getLatestWizardSessionForActivity,
  getLatestWizardSessionForFlow,
  getWizardConversation,
  getWizardPhaseStatusMessage,
  getWizardSession,
  getWizardContextLevel,
  getWizardSessionStatus,
  isAutoDebugSeedText,
  isWizardTerminalStatus,
  linkWizardSessionToFlow,
  listWizardSessions,
  saveWizardFlow,
  resolveWizardFlowApproval,
  sendWizardMessage,
  WIZARD_AUTO_DEBUG_SEED_PREFIX,
  WizardAttachment,
  WizardAutoDebugContext,
  WizardCurrentFlowSnapshot,
  WizardFlowContext,
  WizardMode,
  WizardSessionListItem,
  WizardSessionResponse,
  WizardStreamConnection,
  WizardStreamMessage,
} from "@/flow/services/wizard.service";
import {
  rememberWizardSessionForFlow,
  rememberWizardSessionScope,
  markWizardForReopenAfterRefresh,
  markCurrentWizardScopeForFreshStart,
  shouldKeepFreshWizardScope,
  useWizardSession,
  clearConnectionOfferDecline,
  isConnectionOfferDeclined,
  markConnectionOfferDeclined,
} from "@/flow/composables/useWizardSession";
import type {
  WizardConnectionPrompt,
  WizardContextLevel,
} from "@/flow/services/wizard.service";
import { normalizeWizardProgressStatus } from "@/flow/utils/wizardStatus";
import { getParameter } from "@/core/util";
import { authInfo } from "@/core/auth";
import { TYPE } from "vue-toastification";
import { showAlert } from "@/core/popup";
import i18n from "@/core/i18n/i18n";
import { ElTooltip } from "element-plus";
import WizardChat from "./WizardChat.vue";
import WizardHistory from "./WizardHistory.vue";
import ConnectionSetupDialog from "./ConnectionSetupDialog.vue";

function isAutoDebugSeedMessage(message: {
  role?: string;
  content?: unknown;
}): boolean {
  return message?.role === "user" && isAutoDebugSeedText(message.content);
}

export default defineComponent({
  name: "WizardDrawer",
  components: {
    ElTooltip,
    WizardChat,
    WizardHistory,
    ConnectionSetupDialog,
  },
  props: {
    visible: { type: Boolean, default: false },
    inline: { type: Boolean, default: false },
    flowId: { type: String as PropType<string | null>, default: null },
    flowVersion: { type: Number as PropType<number | null>, default: null },
    flowName: { type: String as PropType<string>, default: "" },
    currentFlowSnapshot: {
      type: Object as PropType<WizardCurrentFlowSnapshot | null>,
      default: null,
    },
    useNewUiEditor: { type: Boolean, default: false },
    skillGenerationEnabled: { type: Boolean, default: true },
    // Optional (Activities host). Pins the mode and hides the Creator/Chat slider
    // so the wizard is chat-only (can diagnose but never mutate a flow). Default
    // null = user-driven mode = the flow editor's existing behaviour (unchanged).
    lockedMode: {
      type: String as PropType<WizardMode | null>,
      default: null,
    },
    // Optional (Activities host). When set, auto-starts a one-shot debug analysis
    // of the given failed run (even while hidden) by seeding a diagnosis request
    // that carries the run's activityId. Emits analysis-started / analysis-ready.
    autoDebugContext: {
      type: Object as PropType<WizardAutoDebugContext | null>,
      default: null,
    },
    // Optional (Activities host). Shows an "Open in flow editor" header action
    // that hands the current chat session off to the editor and navigates there
    // (see @open-in-editor). The flow editor itself never sets this.
    openInEditorEnabled: { type: Boolean, default: false },
  },
  emits: [
    "close",
    "flow-created",
    "history-visibility-change",
    "approval-state-changed",
    "analysis-started",
    "analysis-ready",
    "open-in-editor",
  ],
  setup(props, { emit }) {
    const { t } = i18n.global;
    const {
      flowSpec,
      hydrateSession,
      loading,
      messages,
      mode,
      resetSession,
      sessionId,
      showApproval,
    } = useWizardSession();

    const overlayPhase = ref<"creating" | "opening" | null>(null);
    const overlayAction = ref<"created" | "updated">("created");
    // Pending connection_setup interrupt: the connector the wizard needs a connection
    // for, plus the values it already knows. Null when no setup is in progress.
    const pendingConnectionSetup = ref<any>(null);
    const showConnectionSetup = ref(false);
    // True while the pending request is still waiting for the user to agree to open
    // the form. The form is a modal over the whole chat, so it is never opened off
    // the back of an interrupt alone — the user is asked in the chat first.
    const awaitingConnectionConsent = ref(false);
    const interruptionNotice = ref("");
    const statusMessage = ref("");
    const statusDetailMessage = ref("");
    const historyVisible = ref(false);
    const historyLoading = ref(false);
    const historySessions = ref<WizardSessionListItem[]>([]);
    const hoveredMode = ref<WizardMode | null>(null);
    const pendingStopSessionId = ref<string | null>(null);
    // Holds a client-generated session id so attachments can be uploaded before
    // the first message creates the session. Reused as the new session's id.
    const pendingClientSessionId = ref<string | null>(null);
    let streamConnection: WizardStreamConnection | null = null;
    let pollingTimeoutId: number | null = null;
    let activeTrackingSessionId: string | null = null;
    // Which tracking attempt is current. connectWizardSessionStream resolves and can then invoke
    // onError in the SAME synchronous stack — webstomp dispatches every frame of one WebSocket
    // message in a single forEach, so a CONNECTED+ERROR pair calls both callbacks before our await
    // continuation (a microtask) runs. The fallback re-points activeTrackingSessionId at the same
    // key, so that check alone cannot tell "still mine" from "already superseded"; comparing the
    // generation can. Bumped by stopSessionTracking so ANY teardown retires in-flight connects.
    let trackingGeneration = 0;
    let pollingGenerationInFlight: number | null = null;
    // Last time this session showed a SIGN OF LIFE — not when the wait began. The failure we need
    // to catch is "nothing will ever arrive again" (a run orphaned by a server restart leaves its
    // persisted status at "processing" forever), which is a question about SILENCE, not about
    // elapsed time. An absolute whole-turn deadline fights legitimate long work: the backend budgets
    // SIMULATE_POLL_TIMEOUT_SECONDS (300s) for ONE simulate and allows up to 5 per session inside a
    // 600s anti-thrash window, so a healthy turn can easily outlive any turn-length cap we pick.
    // Measuring idleness instead lets a slow-but-progressing run continue indefinitely while still
    // catching a dead one. See markTrackingActivity for what counts as progress.
    let lastTrackingActivityAt: number | null = null;
    // Last phase seen while polling. A poll that returns the SAME "processing" phase is not
    // progress — an orphaned session answers that way forever — so only a phase CHANGE re-arms.
    let lastTrackingPhase: string | null = null;
    // The staleness check is only USEFUL if something evaluates it. Both call sites are
    // event-driven (a poll tick, or an inbound "processing" message), and neither runs when the
    // STOMP stream connects fine and then goes quiet: no poll loop is started, onError never fires,
    // and an orphaned session publishes nothing. Protocol-level heartbeats keep the socket healthy
    // without invoking onMessage, which makes that state stick. This timer makes the timeout
    // independent of inbound traffic.
    let staleWatchdogId: number | null = null;
    // Operator-tunable via config.json (ConfigMap edit + pod restart, no UI rebuild) because the
    // right value depends on real backend latency. Sized against the longest expected SILENCE, not
    // the longest turn: the quiet gap inside a single simulate is ~5 min (WS progress is emitted on
    // tool_start/tool_end, not during a tool), so 10 min leaves clear headroom. Tighten it if the
    // backend starts emitting a heartbeat during long tools.
    const WIZARD_IDLE_TIMEOUT_FALLBACK_MS = 600000;
    let wizardIdleTimeoutMs = WIZARD_IDLE_TIMEOUT_FALLBACK_MS;
    getParameter("WIZARD_IDLE_TIMEOUT_MS")
      .then((value) => {
        const parsed = Number.parseInt(value, 10);
        if (!Number.isFinite(parsed) || parsed <= 0) {
          return;
        }
        wizardIdleTimeoutMs = parsed;
        // getParameter fetches /config.json, so this can resolve AFTER a session has already
        // armed its watchdog on the fallback budget. Re-arm so the configured value reaches the
        // first session too: nothing else re-arms until the next sign of life, and a session that
        // stays silent from the start — the very case this watchdog exists for — would otherwise
        // keep the fallback deadline for its whole life.
        if (staleWatchdogId !== null && activeTrackingSessionId !== null) {
          armStaleWatchdog(activeTrackingSessionId);
        }
      })
      .catch(() => undefined);
    let activeRequestToken = 0;
    let restoringSession = false;
    let approvalInProgress = false;

    const assistantName = computed(() => t("TeggyAssistant.Name"));
    const betaLabel = computed(() => t("TeggyAssistant.Badge"));
    const currentTitle = computed(() =>
      mode.value === "chat"
        ? t("TeggyAssistant.ChatTitle")
        : t("TeggyAssistant.CreatorTitle")
    );
    const displayTitle = ref(assistantName.value);
    let titleTimer: ReturnType<typeof setTimeout> | null = null;
    // When a host pins the mode (Activities), hide the slider and enforce it:
    // pin on setup + whenever the prop changes, and re-pin if anything flips it.
    const showModeSwitch = computed(() => !props.lockedMode);
    watch(
      () => props.lockedMode as WizardMode | null,
      (locked) => {
        if (locked && mode.value !== locked) {
          mode.value = locked;
        }
      },
      { immediate: true }
    );
    watch(mode, (newMode) => {
      const locked = props.lockedMode as WizardMode | null;
      if (locked && newMode !== locked) {
        mode.value = locked;
        return;
      }
      if (titleTimer) clearTimeout(titleTimer);
      displayTitle.value =
        newMode === "chat"
          ? t("TeggyAssistant.ChatTitle")
          : t("TeggyAssistant.CreatorTitle");
      titleTimer = setTimeout(() => {
        displayTitle.value = assistantName.value;
      }, 2500);
    });
    const switchVisualMode = computed(() => hoveredMode.value || mode.value);
    const canStop = computed(() =>
      Boolean(loading.value && (pendingStopSessionId.value || sessionId.value))
    );
    const currentFlowContext = computed<WizardFlowContext | null>(() => {
      const flowId = props.flowId as string | null;
      const flowVersion = props.flowVersion as number | null;
      const flowName = props.flowName as string;

      if (!flowId || !flowVersion) {
        return null;
      }

      return {
        flowId,
        flowVersion,
        flowName: flowName || undefined,
      };
    });

    function previewMode(nextMode: WizardMode): void {
      if (mode.value !== nextMode) {
        hoveredMode.value = nextMode;
      }
    }

    function clearModeHover(): void {
      hoveredMode.value = null;
    }

    function appendAssistantMessage(content?: string): void {
      if (!content) {
        return;
      }

      const lastMessage = messages.value[messages.value.length - 1];
      if (
        lastMessage?.role === "assistant" &&
        lastMessage.content.trim() === content.trim()
      ) {
        return;
      }

      messages.value.push({
        role: "assistant",
        content,
      });
    }

    function createClientSessionId(): string {
      const cryptoWithUuid = crypto as Crypto & {
        randomUUID?: () => string;
      };

      if (typeof cryptoWithUuid.randomUUID === "function") {
        return `wiz-${cryptoWithUuid.randomUUID()}`;
      }

      return `wiz-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }

    // Provides a stable session id for attachment uploads. Reuses the active
    // session when one exists; otherwise lazily mints a client id (without
    // marking a session as "existing", so the first send still creates it).
    function ensureUploadSessionId(): string {
      if (sessionId.value) {
        return sessionId.value;
      }
      if (!pendingClientSessionId.value) {
        pendingClientSessionId.value = createClientSessionId();
      }
      return pendingClientSessionId.value;
    }

    function isSessionMissingError(error: any): boolean {
      const errorMsg = error?.response?.data?.detail || error?.message || "";
      return (
        typeof errorMsg === "string" &&
        errorMsg.toLowerCase().includes("session not found")
      );
    }

    function clearPollingTimer(): void {
      if (pollingTimeoutId !== null) {
        window.clearTimeout(pollingTimeoutId);
        pollingTimeoutId = null;
      }
    }

    function clearStaleWatchdog(): void {
      if (staleWatchdogId !== null) {
        window.clearTimeout(staleWatchdogId);
        staleWatchdogId = null;
      }
    }

    function stopSessionTracking(): void {
      trackingGeneration += 1;
      activeTrackingSessionId = null;
      lastTrackingActivityAt = null;
      lastTrackingPhase = null;
      clearPollingTimer();
      clearStaleWatchdog();
      disconnectWizardStream(streamConnection);
      streamConnection = null;
    }

    // Fires even when neither the stream nor the poll loop reports anything. Armed for the time
    // remaining in the IDLE budget, so a run that keeps reporting progress keeps pushing the
    // deadline out and never trips.
    function armStaleWatchdog(sessionKey: string): void {
      clearStaleWatchdog();
      const remaining = Math.max(
        0,
        wizardIdleTimeoutMs -
          (Date.now() - (lastTrackingActivityAt ?? Date.now()))
      );
      staleWatchdogId = window.setTimeout(() => {
        staleWatchdogId = null;
        if (activeTrackingSessionId === sessionKey && loading.value) {
          pollWizardSession(sessionKey);
        }
      }, remaining);
    }

    // Record a sign of life and push the deadline out. Called for any inbound stream message (the
    // backend only pushes when something actually happened) and for a poll that reports a NEW phase.
    // Deliberately NOT called for a poll that repeats the same phase: an orphaned session returns
    // "processing" forever, and treating that as progress would restore the permanent spinner.
    function markTrackingActivity(sessionKey: string): void {
      if (activeTrackingSessionId !== sessionKey) {
        return;
      }
      lastTrackingActivityAt = Date.now();
      armStaleWatchdog(sessionKey);
    }

    // True once this session has been SILENT for longer than the idle budget — see
    // lastTrackingActivityAt above.
    function isTrackingStale(): boolean {
      return (
        lastTrackingActivityAt !== null &&
        Date.now() - lastTrackingActivityAt >= wizardIdleTimeoutMs
      );
    }

    function handleStaleProcessingTimeout(): void {
      stopSessionTracking();
      loading.value = false;
      statusMessage.value = "";
      statusDetailMessage.value = "";
      pendingStopSessionId.value = null;
      appendAssistantMessage(
        "Error: This is taking longer than expected and may have been " +
          "interrupted. Your conversation is retained; try again in this chat."
      );
    }

    function setProgressStatus(message?: string, currentPhase?: string): void {
      const phaseMessage =
        typeof currentPhase === "string" && currentPhase.trim()
          ? getWizardPhaseStatusMessage(currentPhase)
          : "";
      const normalizedStatus = normalizeWizardProgressStatus(
        phaseMessage,
        message
      );

      statusMessage.value = normalizedStatus.label;
      statusDetailMessage.value = normalizedStatus.detail;
    }

    function extractFlowSpecFromResponse(response: any) {
      const payload = response?.result || response?.data || {};
      const candidates = [
        payload?.approval_request?.flow_spec,
        payload?.final_flow,
        payload?.flow_spec,
        payload?.result?.approval_request?.flow_spec,
        payload?.result?.final_flow,
        payload?.result?.flow_spec,
        payload?.interrupt?.flow_spec,
        payload?.result?.interrupt?.flow_spec,
        response?.data?.approval_request?.flow_spec,
        response?.data?.final_flow,
        response?.data?.flow_spec,
        response?.data?.interrupt?.flow_spec,
        // AI-296 NEW-26. A finalize approval — the shape `flow_builder` returns once the session
        // owns a draft, which is every session after its first build — carries no `flow_spec` on
        // purpose: the draft is already persisted, and a spec sent back to save-flow is persisted
        // verbatim over its step tree. It carries `flow_preview` instead, a render of the stored
        // flow. Probed after the spec so a payload holding both still prefers the spec.
        payload?.approval_request?.flow_preview,
        payload?.flow_preview,
        payload?.result?.approval_request?.flow_preview,
        payload?.result?.flow_preview,
        payload?.interrupt?.flow_preview,
        payload?.result?.interrupt?.flow_preview,
        response?.data?.approval_request?.flow_preview,
        response?.data?.flow_preview,
        response?.data?.interrupt?.flow_preview,
      ];

      return (
        candidates.find(
          (candidate: any) =>
            candidate &&
            typeof candidate === "object" &&
            Array.isArray(candidate.steps)
        ) || null
      );
    }

    function processResponse(
      response: any,
      options: {
        appendAssistant?: boolean;
        // Restoring a session replays its persisted pending request. Re-opening the
        // flow approval view is wanted there; the connection form must never be
        // forced open — but its in-chat offer is restored, see below.
        restored?: boolean;
      } = {}
    ): void {
      loading.value = false;
      // A completed turn retires the previous offer: if this response no longer asks
      // for a connection, an offer left standing would open a form the wizard has
      // already moved past. The interrupt branch below re-arms it when the request
      // is still live. Skipped while the form is open — the composer stays usable
      // during a turn, so a response can land mid-form, and closing it would throw
      // away what the user had typed in it.
      if (!showConnectionSetup.value) {
        clearConnectionSetup();
      }
      statusMessage.value = "";
      statusDetailMessage.value = "";
      stopSessionTracking();
      pendingStopSessionId.value = null;
      // The level only moves when the conversation does, so it is refetched on turn
      // completion rather than on a timer.
      compacting.value = false;
      refreshContextLevel();

      if (response?.status === "cancelled") {
        interruptionNotice.value = "Interrupted";
        showApproval.value = false;
        flowSpec.value = null;
        // Stop ends a turn, not the conversation. Saved proposals can still be
        // recovered below when reopening a cancelled conversation.
      }

      if (options.appendAssistant !== false) {
        appendAssistantMessage(response.message);
      }

      const payload = response?.result || response?.data || {};
      const finalFlow = extractFlowSpecFromResponse(response);
      const approvalRequest =
        payload?.approval_request ||
        payload?.result?.approval_request ||
        response?.data?.approval_request;
      const legacyInterrupt =
        payload?.interrupt ||
        payload?.result?.interrupt ||
        response?.data?.interrupt;
      const hasFlowBuilderApproval =
        mode.value === "wizard" &&
        finalFlow &&
        ((approvalRequest?.tool === "flow_builder" &&
          approvalRequest?.approval_required !== false) ||
          (legacyInterrupt?.tool === "flow_builder" &&
            legacyInterrupt?.approval_required !== false));

      if (hasFlowBuilderApproval) {
        // Don't re-trigger approval during an active approval flow or if already showing
        if (approvalInProgress || (showApproval.value && flowSpec.value)) {
          return;
        }
        flowSpec.value = finalFlow;
        showApproval.value = true;
        return;
      }

      // A connection_setup interrupt carries no flow_spec — it asks the user to create
      // a connection the wizard needs, in the standard connection form.
      const connectionSetupRequest = [approvalRequest, legacyInterrupt].find(
        (candidate: any) =>
          candidate?.mode === "connection_setup" ||
          candidate?.mode === "connection_edit"
      );

      if (
        mode.value === "wizard" &&
        connectionSetupRequest?.connector_spec_id &&
        !showConnectionSetup.value
      ) {
        // Ask first. The form only opens once the user answers the prompt below the
        // transcript — an interrupt on its own must not take over the screen.
        //
        // This runs on a restored session too (page refresh, or reopening the chat
        // from history). The request stays persisted until the turn moves on, so a
        // replayed one means the conversation really did stop waiting for this
        // answer, and the offer belongs back on screen. What must never come back
        // on its own is the form itself — and it cannot, since only a click on the
        // offer sets `showConnectionSetup`.
        //
        // Unless they already said "Not now": that refusal outlives the reload, and
        // is only forgotten when they send another message (handleSend), which is
        // also the only way a new offer could reach them.
        if (isConnectionOfferDeclined(sessionId.value)) {
          showApproval.value = false;
          return;
        }
        pendingConnectionSetup.value = connectionSetupRequest;
        awaitingConnectionConsent.value = true;
        showApproval.value = false;
        return;
      }

      showApproval.value = false;
    }

    /**
     * Copy for the in-chat consent prompt — the question only. The interrupt's own
     * `prompt` is deliberately not repeated here: the assistant's message has just
     * said the same thing, and the form itself shows that prompt once it opens.
     */
    const connectionPrompt = computed<WizardConnectionPrompt | null>(() => {
      const request = pendingConnectionSetup.value;
      if (!awaitingConnectionConsent.value || !request) {
        return null;
      }

      const isEdit = request.mode === "connection_edit";
      const name = isEdit ? request.connection_name : request.connector_name;
      // Only the plain "nothing to connect with" case may claim no connection was
      // found — the wizard also opens this form to add a second connection or to fix
      // an existing one, and saying otherwise would misstate the user's environment.
      const hasExisting = Number(request.existing_connection_count) > 0;

      let question: string;
      if (isEdit) {
        question = name
          ? t("TeggyAssistant.ConnectionEditConfirmFor", { name })
          : t("TeggyAssistant.ConnectionEditConfirm");
      } else if (hasExisting && name) {
        question = t("TeggyAssistant.ConnectionSetupConfirmAnother", { name });
      } else if (name) {
        question = t("TeggyAssistant.ConnectionSetupConfirmFor", { name });
      } else {
        question = t("TeggyAssistant.ConnectionSetupConfirm");
      }

      return { question };
    });

    /** The user agreed — open the form they were just told about. */
    function openConnectionSetup(): void {
      if (!pendingConnectionSetup.value) {
        return;
      }
      awaitingConnectionConsent.value = false;
      showConnectionSetup.value = true;
    }

    /**
     * The user declined. The request is dropped so the prompt does not linger, and
     * nothing is sent to the wizard: the turn has already ended, so they can simply
     * say what they want next — and the wizard asks again if it still needs one.
     *
     * The refusal is remembered across reloads. Without that, the server replays the
     * pending request on every restore and the offer they just declined would be
     * back on screen after each refresh.
     */
    function dismissConnectionSetup(): void {
      // Dismiss first: persisting is best-effort (private windows, blocked or full
      // storage), and a failure there must not leave the card sitting on screen as
      // though the click did nothing.
      const declinedSessionId = sessionId.value;
      clearConnectionSetup();
      markConnectionOfferDeclined(declinedSessionId);
    }

    // Closing the form without saving (X, Esc) leaves the wizard still waiting on a
    // connection, so the prompt comes back rather than the request going silent.
    watch(showConnectionSetup, (open, wasOpen) => {
      if (wasOpen && !open && pendingConnectionSetup.value) {
        awaitingConnectionConsent.value = true;
      }
    });

    function clearConnectionSetup(): void {
      awaitingConnectionConsent.value = false;
      showConnectionSetup.value = false;
      pendingConnectionSetup.value = null;
    }

    /**
     * Resume the paused turn after the user saves the connection.
     *
     * Sent silently: the synthetic message is not shown in the transcript, so the
     * conversation reads as though the assistant simply carried on.
     */
    async function onConnectionCreated(payload: {
      connectionId?: string;
      name?: string;
    }): Promise<void> {
      const wasEdit = pendingConnectionSetup.value?.mode === "connection_edit";
      awaitingConnectionConsent.value = false;
      pendingConnectionSetup.value = null;
      clearConnectionOfferDecline(sessionId.value);
      const described = payload?.name
        ? `"${payload.name}"`
        : "the requested connection";
      const action = wasEdit ? "been updated" : "been created";
      await sendToWizard(
        `The connection ${described} has ${action} and is ready to use. Continue from where you left off.`,
        undefined,
        undefined,
        { silent: true }
      );
    }

    async function restoreConversation(
      sessionKey: string,
      replaceExisting = false
    ): Promise<void> {
      const requestToken = activeRequestToken;
      const conversation = await getWizardConversation(sessionKey);
      if (sessionId.value !== sessionKey || requestToken !== activeRequestToken)
        return;

      if (
        conversation.conversationHistory.length > 0 &&
        (replaceExisting || messages.value.length === 0)
      ) {
        // Strip the internal auto-debug seed turn — the backend legitimately
        // stored it as a real user message, but it must never be shown (see
        // AUTO_DEBUG_SEED_PREFIX).
        messages.value = conversation.conversationHistory.filter(
          (message) => !isAutoDebugSeedMessage(message)
        );
      }
    }

    async function syncLatestFlowSession(): Promise<void> {
      // Editor-only behaviour: "resume whatever chat I last had about this
      // flow+version". The Activities host (lockedMode set) scopes sessions
      // per RUN (activityId), and a single flow version can have many runs —
      // resuming "the latest session for this flow" here would silently swap
      // in an unrelated run's conversation (wrong diagnosis, or someone
      // else's in-flight "analyzing" state bleeding into this run's panel).
      if (props.lockedMode) {
        return;
      }

      const flowContext = currentFlowContext.value;
      if (!flowContext) {
        return;
      }

      const flowScopeKey = `${flowContext.flowId}:${flowContext.flowVersion}`;
      if (shouldKeepFreshWizardScope(`flow:${flowScopeKey}`)) {
        return;
      }

      try {
        const latestSession = await getLatestWizardSessionForFlow(
          flowContext.flowId,
          flowContext.flowVersion
        );

        if (!latestSession || latestSession.sessionId === sessionId.value) {
          return;
        }

        // Never clobber an in-flight session with a stale server lookup result.
        // hydrateSession resets loading to false, which would drop the active
        // processing UI even though a request is still in progress.
        if (loading.value) {
          return;
        }

        hydrateSession({
          sessionId: latestSession.sessionId,
          mode: latestSession.mode,
        });
      } catch {
        // Keep the local session state if the latest-session lookup fails.
      }
    }

    // The auto-debug seed is stored server-side as the session's first user
    // message, so listWizardSessions' `preview` is the raw seed text for
    // those sessions — redact before it ever reaches the (presentation-only)
    // WizardHistory list.
    function toDisplaySessions(
      sessions: WizardSessionListItem[]
    ): WizardSessionListItem[] {
      return sessions.map((session) =>
        isAutoDebugSeedText(session.preview)
          ? { ...session, preview: t("TeggyAssistant.DebugSessionPreview") }
          : session
      );
    }

    async function loadHistory(): Promise<void> {
      historyLoading.value = true;

      try {
        const response = await listWizardSessions(30);
        historySessions.value = toDisplaySessions(response.sessions);
      } catch {
        historySessions.value = [];
      } finally {
        historyLoading.value = false;
      }
    }

    async function pollWizardSession(sessionKey: string): Promise<void> {
      const generation = trackingGeneration;
      const isCurrent = () =>
        generation === trackingGeneration &&
        activeTrackingSessionId === sessionKey &&
        sessionId.value === sessionKey;
      if (!isCurrent() || pollingGenerationInFlight === generation) return;
      clearPollingTimer();
      pollingGenerationInFlight = generation;
      try {
        const status = await getWizardSessionStatus(sessionKey);
        if (!isCurrent()) return;
        if (!isWizardTerminalStatus(status.status)) {
          const phase = status.currentPhase ?? null;
          if (phase !== lastTrackingPhase) {
            lastTrackingPhase = phase;
            markTrackingActivity(sessionKey);
          }
          if (isTrackingStale()) {
            handleStaleProcessingTimeout();
            return;
          }
          setProgressStatus(undefined, status.currentPhase);
          return;
        }
        const [sessionResponse, conversation] = await Promise.all([
          getWizardSession(sessionKey),
          getWizardConversation(sessionKey),
        ]);
        if (!isCurrent()) return;
        // A new turn may have started between the two reads (another tab).
        if (!isWizardTerminalStatus(sessionResponse.status)) return;
        if (conversation.conversationHistory.length) {
          messages.value = conversation.conversationHistory.filter(
            (message) => !isAutoDebugSeedMessage(message)
          );
        }
        // This is completion of a live turn: connection setup must open normally.
        processResponse(sessionResponse, { appendAssistant: false });
      } catch (error: any) {
        if (!isCurrent()) return;
        if (isSessionMissingError(error) || isTrackingStale()) {
          handleStaleProcessingTimeout();
        }
      } finally {
        if (pollingGenerationInFlight === generation)
          pollingGenerationInFlight = null;
        if (isCurrent()) {
          pollingTimeoutId = window.setTimeout(
            () => pollWizardSession(sessionKey),
            2500
          );
        }
      }
    }

    // -- context / compaction (AI-291) -------------------------------------
    // Owned here rather than in WizardChat because this component owns the session
    // and the stream; the chat only renders what it is given.
    const contextLevel = ref<WizardContextLevel | null>(null);
    const contextMenuOpen = ref(false);
    const compacting = ref(false);
    const contextError = ref("");

    async function refreshContextLevel(): Promise<void> {
      const sessionKey = sessionId.value;
      if (!sessionKey) {
        contextLevel.value = null;
        return;
      }

      try {
        const level = await getWizardContextLevel(sessionKey);
        // Discard a reply that arrived after the user switched conversations.
        if (sessionId.value === sessionKey) {
          contextLevel.value = level;
        }
      } catch {
        // The meter is informational: a failed read must not disturb the chat.
        contextLevel.value = null;
      }
    }

    function toggleContextMenu(): void {
      contextMenuOpen.value = !contextMenuOpen.value;
      if (contextMenuOpen.value) {
        contextError.value = "";
        refreshContextLevel();
      }
    }

    async function handleCompactContext(): Promise<void> {
      const sessionKey = sessionId.value;
      if (!sessionKey || compacting.value) {
        return;
      }

      compacting.value = true;
      contextError.value = "";
      try {
        const result = await compactWizardSession(sessionKey);
        if (sessionId.value === sessionKey) {
          contextLevel.value = result.context;
          // A thread that could not be summarized still holds its full history, so
          // this is reported rather than passed off as a success.
          if (Object.keys(result.failedThreads).length > 0) {
            contextError.value = t("TeggyAssistant.ContextCompactFailed");
          }
        }
      } catch {
        contextError.value = t("TeggyAssistant.ContextCompactFailed");
      } finally {
        compacting.value = false;
      }
    }

    function handleStreamMessage(message: WizardStreamMessage): void {
      if (message.sessionId !== sessionId.value) return;

      // The backend pushes only when something happened, so any message is progress.
      markTrackingActivity(message.sessionId);
      if (message.status === "processing") {
        loading.value = true;
        // Compaction rides on "processing" with a flag: WizardSessionStatus is a
        // closed union that unrelated flow logic branches on.
        const carriesCompactionFlag =
          typeof message.data?.compacting === "boolean";
        if (carriesCompactionFlag) {
          compacting.value = Boolean(message.data?.compacting);
        }
        // The "compaction finished" push carries the flag and no text. Passing its
        // empty message to setProgressStatus would blank the status line the turn
        // is still using, so a flag-only update leaves the text alone.
        if (!(carriesCompactionFlag && !message.message)) {
          setProgressStatus(message.message, message.data?.phase);
        }
        return;
      }
      // Notifications can be dropped, duplicated or delayed across a Stop/resume.
      // Only the authenticated durable session response may finish the current turn.
      pollWizardSession(message.sessionId);
    }

    async function startSessionTracking(sessionKey: string): Promise<void> {
      stopSessionTracking();
      const generation = trackingGeneration;
      activeTrackingSessionId = sessionKey;
      lastTrackingActivityAt = Date.now();
      armStaleWatchdog(sessionKey);
      // Reconcile even if the WebSocket connects successfully and then stays silent.
      pollingTimeoutId = window.setTimeout(
        () => pollWizardSession(sessionKey),
        2500
      );
      let streamRetired = false;
      const isCurrentAttempt = () =>
        !streamRetired &&
        trackingGeneration === generation &&
        activeTrackingSessionId === sessionKey;
      try {
        const connection = await connectWizardSessionStream(sessionKey, {
          onMessage: (message) => {
            if (isCurrentAttempt()) handleStreamMessage(message);
          },
          onError: () => {
            if (!isCurrentAttempt()) return;
            streamRetired = true;
            disconnectWizardStream(streamConnection);
            streamConnection = null;
            pollWizardSession(sessionKey);
          },
        });
        if (!isCurrentAttempt()) {
          disconnectWizardStream(connection);
          return;
        }
        streamConnection = connection;
      } catch {
        if (isCurrentAttempt()) {
          streamRetired = true;
          pollWizardSession(sessionKey);
        }
      }
    }

    async function syncPersistedSession(): Promise<void> {
      if (!props.visible || restoringSession) {
        return;
      }

      restoringSession = true;
      const requestToken = activeRequestToken;

      try {
        await syncLatestFlowSession();

        if (requestToken !== activeRequestToken || !sessionId.value) {
          return;
        }

        const currentSessionId = sessionId.value;
        pendingStopSessionId.value = currentSessionId;

        if (messages.value.length === 0) {
          await restoreConversation(currentSessionId, true);
        }

        const sessionResponse = await getWizardSession(currentSessionId);

        if (
          requestToken !== activeRequestToken ||
          sessionId.value !== currentSessionId
        ) {
          return;
        }

        if (sessionResponse.status === "processing") {
          loading.value = true;
          setProgressStatus(undefined, sessionResponse.currentPhase);
          // Only (re)start tracking when we're not already watching this session.
          // startSessionTracking calls stopSessionTracking first, so calling it
          // unnecessarily kills the live stream that is already delivering updates.
          if (activeTrackingSessionId !== currentSessionId) {
            await startSessionTracking(currentSessionId);
          }
          return;
        }

        processResponse(sessionResponse, {
          appendAssistant: false,
          restored: true,
        });
      } catch (error: any) {
        if (requestToken !== activeRequestToken) return;
        const errorMsg =
          error?.response?.data?.detail ||
          error?.message ||
          "Failed to restore session";
        console.warn("Failed to restore wizard session.", error);
        stopSessionTracking();
        loading.value = false;
        interruptionNotice.value = "";
        statusMessage.value = "";
        statusDetailMessage.value = "";
        pendingStopSessionId.value = null;
        appendAssistantMessage(`Error: Failed to restore session. ${errorMsg}`);
      } finally {
        restoringSession = false;
      }
    }

    async function requestWizardResponse(
      message: string,
      useExistingSession: boolean,
      requestSessionId?: string,
      attachmentIds?: string[]
    ): Promise<WizardSessionResponse> {
      // Capture the editor context for this request.
      const { currentFlowSnapshot } = props;
      // Send the run being debugged on each turn to retain its failed-run context.
      //
      // Only while the LOADED session is still the one that run belongs to. autoDebugContext is
      // a host prop and does not change when the user picks another conversation from history —
      // handleSelectSession clears debugSessionActivityId precisely because the new session is
      // not known to be about that run. Without this check, session B would be anchored to
      // session A's activity_id and later diagnosed against the wrong run.
      const autoDebugContext =
        props.autoDebugContext as WizardAutoDebugContext | null;
      const debugContextPayload = buildDebugContextPayload(
        autoDebugContext &&
          debugSessionActivityId.value === autoDebugContext.activityId
          ? autoDebugContext
          : null,
        currentFlowContext.value
      );

      if (!useExistingSession) {
        return createWizardSession(
          message,
          mode.value,
          requestSessionId,
          currentFlowContext.value || undefined,
          currentFlowSnapshot,
          props.skillGenerationEnabled,
          attachmentIds,
          debugContextPayload
        );
      }
      return sendWizardMessage(
        requestSessionId || sessionId.value,
        message,
        mode.value,
        currentFlowSnapshot,
        props.skillGenerationEnabled,
        attachmentIds,
        debugContextPayload
      );
    }

    async function sendToWizard(
      message: string,
      attachmentIds?: string[],
      attachments?: WizardAttachment[],
      options: { silent?: boolean } = {}
    ): Promise<void> {
      const requestToken = ++activeRequestToken;
      const hadExistingSession = Boolean(sessionId.value);
      const requestSessionId =
        sessionId.value ||
        pendingClientSessionId.value ||
        createClientSessionId();

      interruptionNotice.value = "";
      pendingStopSessionId.value = requestSessionId;
      if (!sessionId.value) {
        sessionId.value = requestSessionId;
      }
      // Consumed — the next new session will mint a fresh id.
      pendingClientSessionId.value = null;
      // `silent`: internally-generated instructions (the auto-debug seed, or resuming
      // after a connection is created) that must still reach the backend but are not
      // something the user typed — keep them out of the transcript so the conversation
      // reads naturally and the user only sees the agent's own response.
      if (!options.silent) {
        messages.value.push({
          role: "user",
          content: message,
          // Stamp the optimistic message so the @-mention picker's "attached when"
          // hint reflects real elapsed time within the session (otherwise these
          // files would read "Just added" forever until a history reload).
          timestamp: new Date().toISOString(),
          ...(attachments && attachments.length ? { attachments } : {}),
        });
      }
      loading.value = true;
      statusMessage.value = "";
      statusDetailMessage.value = "";
      showApproval.value = false;
      flowSpec.value = null;
      stopSessionTracking();

      try {
        const response = await requestWizardResponse(
          message,
          hadExistingSession,
          requestSessionId,
          attachmentIds
        );

        if (requestToken !== activeRequestToken) {
          return;
        }

        sessionId.value = response.sessionId;

        if (response.status === "processing") {
          setProgressStatus(response.message, response.currentPhase);
          await startSessionTracking(response.sessionId);
          return;
        }

        processResponse(response);
      } catch (error: any) {
        if (requestToken !== activeRequestToken) {
          return;
        }

        const errorMsg =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to communicate with wizard";
        appendAssistantMessage(`Error: ${errorMsg}`);
        loading.value = false;
        statusMessage.value = "";
        statusDetailMessage.value = "";
        stopSessionTracking();
        if (!hadExistingSession) {
          sessionId.value = null;
        }
        pendingStopSessionId.value = null;
      }
    }

    async function handleSend(
      payload:
        | string
        | {
            text: string;
            attachmentIds?: string[];
            attachments?: WizardAttachment[];
          }
    ): Promise<void> {
      const message = typeof payload === "string" ? payload : payload.text;
      const attachmentIds =
        typeof payload === "string" ? undefined : payload.attachmentIds;
      const attachments =
        typeof payload === "string" ? undefined : payload.attachments;
      // Asking again ("try that connection again", or just restating the request) is
      // how a declined offer is taken back. Only the user's own messages count —
      // the silent resume after a save goes through sendToWizard directly.
      clearConnectionOfferDecline(sessionId.value);
      await sendToWizard(message, attachmentIds, attachments);
      loadHistory().catch(() => undefined);
    }

    async function handleStop(): Promise<void> {
      const activeSessionId = pendingStopSessionId.value || sessionId.value;
      if (!activeSessionId) {
        return;
      }

      const requestToken = ++activeRequestToken;
      stopSessionTracking();

      try {
        await cancelWizardSession(activeSessionId);
      } catch {
        // Stop the local waiting state even if cancellation fails remotely.
      }

      if (
        requestToken !== activeRequestToken ||
        sessionId.value !== activeSessionId
      )
        return;
      stopSessionTracking();
      loading.value = false;
      statusMessage.value = "";
      statusDetailMessage.value = "";
      interruptionNotice.value = "Interrupted";
      showApproval.value = false;
      flowSpec.value = null;
      pendingStopSessionId.value = null;
      loadHistory().catch(() => undefined);
    }

    function switchMode(nextMode: WizardMode): void {
      // Host has pinned the mode (Activities chat) — never switch.
      if (props.lockedMode) {
        return;
      }
      clearModeHover();

      if (mode.value === nextMode) {
        return;
      }

      stopSessionTracking();
      historyVisible.value = false;
      interruptionNotice.value = "";
      statusMessage.value = "";
      statusDetailMessage.value = "";
      pendingStopSessionId.value = null;

      // If there is an active session, stay in it and insert a visual divider.
      // Only reset when there is no session (nothing to preserve).
      if (sessionId.value && messages.value.length > 0) {
        // Increment token so any in-flight sendToWizard response is discarded.
        activeRequestToken += 1;
        loading.value = false;
        showApproval.value = false;
        flowSpec.value = null;
        messages.value = [
          ...messages.value,
          { role: "mode_divider", content: "", toMode: nextMode },
        ];
        mode.value = nextMode;
      } else {
        activeRequestToken += 1;
        resetWizardSession(true);
        mode.value = nextMode;
      }

      loadHistory().catch(() => undefined);
    }

    async function toggleHistory(): Promise<void> {
      historyVisible.value = !historyVisible.value;

      if (historyVisible.value) {
        await loadHistory();
      }
    }

    async function handleSelectSession(
      selectedSession: WizardSessionListItem
    ): Promise<void> {
      activeRequestToken += 1;
      stopSessionTracking();
      historyVisible.value = false;
      interruptionNotice.value = "";
      statusMessage.value = "Opening";
      statusDetailMessage.value = "";
      loading.value = true;
      showApproval.value = false;
      flowSpec.value = null;
      // Switching sessions must not carry another session's connection request over.
      clearConnectionSetup();
      mode.value = selectedSession.mode;
      sessionId.value = selectedSession.sessionId;
      messages.value = [];
      // The user explicitly picked a different conversation — it's no longer
      // known to be "for" whatever activityId the last auto-debug covered.
      debugSessionActivityId.value = null;
      await syncPersistedSession();
    }

    watch(
      () => props.visible,
      (nextVisible) => {
        if (nextVisible) {
          // If a session is already in flight, the stream/poll tracking loop
          // handles all updates. Running syncPersistedSession here would make
          // redundant API calls and risk clobbering loading state (e.g. via
          // hydrateSession) even though the backend is still processing.
          if (!loading.value) {
            syncPersistedSession();
          }
          loadHistory().catch(() => undefined);
          return;
        }

        // Inline drawers are temporarily hidden when the user opens another
        // panel. Keep the in-flight wizard session alive so loading/progress
        // state is still there when they come back.
        if (props.inline) {
          return;
        }

        activeRequestToken += 1;
        stopSessionTracking();
        overlayPhase.value = null;
        interruptionNotice.value = "";
        statusMessage.value = "";
        statusDetailMessage.value = "";
        historyVisible.value = false;
      },
      { immediate: true }
    );

    // Fetch the context level as soon as there is a thread to describe — a new
    // session, one restored from history, or a switch between conversations.
    // Without this the level was only fetched when a turn finished, so any
    // conversation whose turns predated the page load showed a permanently grey,
    // tooltip-less ring.
    watch(
      sessionId,
      (nextSessionId) => {
        contextMenuOpen.value = false;
        contextError.value = "";
        if (nextSessionId) {
          refreshContextLevel();
        } else {
          contextLevel.value = null;
        }
      },
      { immediate: true }
    );

    watch(
      historyVisible,
      (isVisible) => {
        emit("history-visibility-change", isVisible);
      },
      { immediate: true }
    );

    watch([showApproval, flowSpec, loading], () => {
      emit("approval-state-changed", {
        active: showApproval.value && mode.value === "wizard",
        flowSpec: flowSpec.value,
        loading: loading.value,
      });
    });

    // --- Auto debug kickoff (Activities host + editor Run Flow/Simulate Step) ---
    // Start the analysis once per run (even while hidden), unless a diagnosis for
    // this run already exists in the restored session (dedupe on reopen).
    const autoDebugStartedFor = ref<string | null>(null);
    // Which activityId the CURRENTLY LOADED session/messages actually belong to.
    // sessionId/messages are module-level singletons shared with every other
    // wizard usage — without this, a session carried over from an unrelated
    // context (e.g. the Activities "Open in flow editor" hand-off) gets
    // mistaken for "already analyzed this run", and a fresh Run Flow/Simulate
    // Step analysis silently no-ops instead of starting.
    const debugSessionActivityId = ref<string | null>(null);
    // The activityId a pending sendToWizard() completion should be reported
    // for — not just a boolean. resetSession()/hydrateSession() both set
    // loading.value = false as a side effect (clearing state for a NEW
    // context), which fires the watch(loading, ...) below even though no
    // request actually completed; capturing the specific activityId (and
    // clearing it before either of those side-effecting calls run, see
    // maybeAutoStartDebug) stops that spurious transition from being
    // misreported as a real completion for the wrong run.
    let awaitingAutoDebugResultFor: string | null = null;

    function buildDebugSeedMessage(activityId: string): string {
      return (
        `${WIZARD_AUTO_DEBUG_SEED_PREFIX} ` +
        `The failed run id (activity_id) is ${activityId}. ` +
        "Investigate why it failed and tell me the root cause, where it broke, and how to fix it."
      );
    }

    // resetSession() is the shared useWizardSession() composable, used by
    // other components too, so it can't own WizardDrawer-local state. Every
    // place that resets the session here must also forget which activityId
    // it was tracking — routing through this wrapper (instead of each call
    // site remembering to clear debugSessionActivityId itself) keeps that
    // invariant in one place, since a missed call site previously let a
    // stale debugSessionActivityId survive a close/mode-switch and made
    // maybeAutoStartDebug misjudge whether a freshly (re)opened run's
    // session was already covered.
    function resetWizardSession(preserveMode = true): void {
      resetSession(preserveMode);
      debugSessionActivityId.value = null;
      // The consent prompt lives in the transcript — a new or closed chat must not
      // keep asking about the old one's connection.
      clearConnectionSetup();
    }

    async function maybeAutoStartDebug(): Promise<void> {
      const ctx = props.autoDebugContext as WizardAutoDebugContext | null;
      if (!ctx || !ctx.activityId) {
        return;
      }
      if (autoDebugStartedFor.value === ctx.activityId) {
        return;
      }
      autoDebugStartedFor.value = ctx.activityId;

      // A conversation for THIS run is already loaded locally (restored via
      // the sessionStorage scope cache) — don't re-run. Gated on
      // debugSessionActivityId, not just "some session happens to be
      // loaded": the loaded session could belong to a different run
      // entirely (e.g. carried over from Activities), in which case it must
      // NOT be treated as covering this one.
      if (
        debugSessionActivityId.value === ctx.activityId &&
        (sessionId.value || messages.value.length > 0)
      ) {
        emit("analysis-ready", { activityId: ctx.activityId, reused: true });
        return;
      }

      // We're committing to superseding whatever the loaded session
      // currently represents (via hydrate or reset below) — any previously
      // pending completion is now moot: sendToWizard's own activeRequestToken
      // check already keeps a stale call from touching messages/loading, but
      // without this it could still masquerade as "this run's" completion
      // when resetSession/hydrateSession's loading=false side effect fires
      // the watcher below.
      awaitingAutoDebugResultFor = null;

      // The sessionStorage scope cache doesn't survive a fresh tab/browser
      // session. Before starting a brand-new (costly) analysis, do a fast
      // backend lookup for a prior session that already diagnosed this exact
      // run — the diagnosis is already saved server-side (Elasticsearch), so
      // this should be near-instant and never show the "analyzing" toast.
      const flowId = props.flowId as string | null;
      const flowVersion = props.flowVersion as number | null;
      if (flowId && flowVersion) {
        try {
          const existing = await getLatestWizardSessionForActivity(
            flowId,
            flowVersion,
            ctx.activityId
          );
          // Bail if autoDebugContext moved on to a different run while awaiting.
          if (autoDebugStartedFor.value !== ctx.activityId) {
            return;
          }
          if (existing) {
            // A different run's request may still be in flight (its
            // activeRequestToken check will now discard it as stale) and/or
            // still streaming/polling — invalidate and tear both down before
            // swapping the shared session state out from under them.
            activeRequestToken += 1;
            stopSessionTracking();
            hydrateSession({
              sessionId: existing.sessionId,
              mode: existing.mode,
            });
            debugSessionActivityId.value = ctx.activityId;
            rememberWizardSessionScope(
              `activity:${ctx.activityId}`,
              existing.sessionId,
              existing.mode
            );
            emit("analysis-ready", {
              activityId: ctx.activityId,
              reused: true,
            });
            return;
          }
        } catch {
          // Fall through and start a fresh analysis if the lookup fails.
        }
      }

      // Starting fresh for a run the loaded session doesn't already cover —
      // clear any stale session/messages first (e.g. carried over from
      // Activities) so this analysis creates its OWN new session instead of
      // being appended as a follow-up onto an unrelated conversation.
      if (debugSessionActivityId.value !== ctx.activityId) {
        resetWizardSession(true);
      }
      debugSessionActivityId.value = ctx.activityId;

      awaitingAutoDebugResultFor = ctx.activityId;
      emit("analysis-started", { activityId: ctx.activityId });
      await sendToWizard(
        buildDebugSeedMessage(ctx.activityId),
        undefined,
        undefined,
        {
          silent: true,
        }
      );
    }

    watch(
      () => props.autoDebugContext,
      () => {
        maybeAutoStartDebug().catch(() => undefined);
      },
      { immediate: true, deep: true }
    );

    // Announce completion of an auto-started analysis so the host can toast.
    // The wizard surfaces failures as an inline "Error: …" assistant message
    // (its convention), so flag that as a failed analysis for a red toast.
    watch(loading, (isLoading) => {
      if (!isLoading && awaitingAutoDebugResultFor) {
        const completedActivityId = awaitingAutoDebugResultFor;
        awaitingAutoDebugResultFor = null;
        const last = messages.value[messages.value.length - 1];
        const failed =
          !!last &&
          last.role === "assistant" &&
          typeof last.content === "string" &&
          last.content.startsWith("Error:");
        emit("analysis-ready", {
          activityId: completedActivityId,
          reused: false,
          failed,
        });
      }
    });

    onBeforeUnmount(() => {
      if (titleTimer) {
        clearTimeout(titleTimer);
        titleTimer = null;
      }
      stopSessionTracking();
    });

    async function handleApprove(): Promise<void> {
      if (!flowSpec.value) return;
      if (!authInfo.value?.organizationId) {
        showAlert(
          TYPE.ERROR,
          "Please select an organization before creating a flow."
        );
        return;
      }

      approvalInProgress = true;
      overlayPhase.value = "creating";
      overlayAction.value = currentFlowContext.value ? "updated" : "created";
      loading.value = true;
      statusMessage.value = "";
      statusDetailMessage.value = "";
      stopSessionTracking();

      try {
        // `saved_flow_snapshot` marks a RENDER of a flow the gateway already holds — what
        // `build_saved_flow_preview` produces for a finalize or staged-ops approval. It is not a
        // specification, and save-flow persists `flow_spec` verbatim through
        // `build_and_persist_flow`, a full `setSteps` replacement (AI-296 F-009/F-015). Sending a
        // snapshot there would overwrite the very tree simulate had verified. The server saves
        // these from `pending_approval` (`finalize_draft` / `flow_ops`), so the body wants no spec.
        const specToPersist = (flowSpec.value as any)?.saved_flow_snapshot
          ? undefined
          : flowSpec.value;
        const persistedFlow = await saveWizardFlow(
          sessionId.value ?? "",
          currentFlowContext.value,
          props.useNewUiEditor,
          specToPersist
        );
        overlayAction.value = persistedFlow.action;
        overlayPhase.value = "opening";
        showApproval.value = false;
        flowSpec.value = null;
        interruptionNotice.value =
          persistedFlow.action === "updated" ? "Flow updated" : "Flow created";

        if (sessionId.value) {
          try {
            await linkWizardSessionToFlow(sessionId.value, {
              flowId: persistedFlow.id,
              flowVersion: persistedFlow.version,
              flowName: persistedFlow.name,
            });
            rememberWizardSessionForFlow(
              persistedFlow,
              sessionId.value,
              mode.value
            );
          } catch (error) {
            console.warn(
              "Wizard flow persisted but session restore link failed.",
              error
            );
            showAlert(
              TYPE.WARNING,
              "Flow saved, but reopening the wizard conversation may not work until the session is refreshed."
            );
          }

          try {
            await resolveWizardFlowApproval(sessionId.value, {
              flowContext: {
                flowId: persistedFlow.id,
                flowVersion: persistedFlow.version,
                flowName: persistedFlow.name,
              },
            });
          } catch (error) {
            console.warn(
              "Wizard flow persisted but approval resolution failed.",
              error
            );
            showAlert(
              TYPE.WARNING,
              "Flow saved, but the wizard session may need a refresh before normal follow-up chat works."
            );
          }
        }

        markWizardForReopenAfterRefresh();
        loadHistory().catch(() => undefined);
        setTimeout(() => {
          overlayPhase.value = null;
          emit("flow-created", persistedFlow);
          emit("close");
        }, 900);
      } catch (error: any) {
        overlayPhase.value = null;
        const errorMsg =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          "Failed to persist flow";
        showAlert(TYPE.ERROR, errorMsg);
      } finally {
        approvalInProgress = false;
        loading.value = false;
      }
    }

    async function handleReject(): Promise<void> {
      const currentSessionId = sessionId.value;
      const requestToken = ++activeRequestToken;

      stopSessionTracking();
      showApproval.value = false;
      flowSpec.value = null;
      interruptionNotice.value = "";
      statusMessage.value = "";
      statusDetailMessage.value = "";

      if (!currentSessionId) {
        interruptionNotice.value = "Cancelled";
        return;
      }

      pendingStopSessionId.value = currentSessionId;
      loading.value = true;

      try {
        const response = await sendWizardMessage(
          currentSessionId,
          "no",
          mode.value,
          props.currentFlowSnapshot,
          props.skillGenerationEnabled
        );

        if (
          requestToken !== activeRequestToken ||
          sessionId.value !== currentSessionId
        ) {
          return;
        }

        sessionId.value = response.sessionId;

        if (response.status === "processing") {
          setProgressStatus(response.message, response.currentPhase);
          await startSessionTracking(response.sessionId);
        } else {
          processResponse(response);
        }
      } catch (error: any) {
        if (requestToken !== activeRequestToken) {
          return;
        }

        appendAssistantMessage(
          `Error: ${
            error?.response?.data?.detail ||
            error?.message ||
            "Failed to communicate with wizard"
          }`
        );
        loading.value = false;
        pendingStopSessionId.value = null;
        interruptionNotice.value = "Cancelled";
      } finally {
        loadHistory().catch(() => undefined);
      }
    }

    async function handleFeedback(feedback: string): Promise<void> {
      showApproval.value = false;
      flowSpec.value = null;
      await sendToWizard(feedback);
      loadHistory().catch(() => undefined);
    }

    function handleNewChat(): void {
      activeRequestToken += 1;
      stopSessionTracking();
      historyVisible.value = false;
      interruptionNotice.value = "";
      statusMessage.value = "";
      statusDetailMessage.value = "";
      markCurrentWizardScopeForFreshStart();
      resetWizardSession(true);
    }

    function handleClose(): void {
      activeRequestToken += 1;
      stopSessionTracking();
      overlayPhase.value = null;
      historyVisible.value = false;
      interruptionNotice.value = "";
      statusMessage.value = "";
      statusDetailMessage.value = "";
      resetWizardSession(true);
      emit("close");
    }

    function handleBackdropClick(): void {
      if (!props.inline) {
        handleClose();
      }
    }

    return {
      canStop,
      betaLabel,
      currentTitle,
      displayTitle,
      flowSpec,
      handleApprove,
      handleBackdropClick,
      handleClose,
      handleFeedback,
      handleNewChat,
      handleReject,
      handleSelectSession,
      handleSend,
      handleStop,
      ensureUploadSessionId,
      historyLoading,
      historySessions,
      historyVisible,
      loading,
      messages,
      contextLevel,
      contextMenuOpen,
      compacting,
      contextError,
      toggleContextMenu,
      handleCompactContext,
      mode,
      overlayAction,
      overlayPhase,
      previewMode,
      sessionId,
      showApproval,
      showModeSwitch,
      showConnectionSetup,
      connectionSetupRequest: pendingConnectionSetup,
      connectionPrompt,
      openConnectionSetup,
      dismissConnectionSetup,
      onConnectionCreated,
      interruptionNotice,
      switchVisualMode,
      statusMessage,
      statusDetailMessage,
      switchMode,
      toggleHistory,
      clearModeHover,
      t,
    };
  },
});
